import { ExhibitorData }       from 'forms/AddStandMultiStepsForm/types';
import { useMemo }             from 'react';
import { useState }            from 'react';
import { Col }                 from 'antd';
import { Button }              from 'antd';
import { Form }                from 'antd';
import { observer }            from 'mobx-react';
import Input                   from 'components/App/IntlInput';
import Select                  from 'components/App/IntlSelect';
import Row                     from 'components/App/Row';
import UserModel               from 'models/UserModel';
import { ApiCollection }       from 'collections/ApiCollection';
import FormattedMessageWrapper from 'components/App/FormattedMessageWrapper';
import { ROLE_STANDIST }       from 'constants/RoleReferences';
import { EN, FR }              from 'constants/Languages';

const { Option } = Select;

interface StepExhibitorProps {
  clearExhibitorData: () => void;
  exhibitor: ExhibitorData;
  setExhibitor: (exhibitorData: ExhibitorData) => void;
}

const StepExhibitor = observer(({
  clearExhibitorData,
  exhibitor,
  setExhibitor,
}: StepExhibitorProps) => {
  const [selectValue, setSelectValue] = useState<id>();

  const exhibitorCollection = useMemo(() => new ApiCollection(UserModel), []);

  const fetchSelectData = async (value: string) => {
    exhibitorCollection.clear()
    if (value.length >= 2) {
      await exhibitorCollection
        .setFilters({
          'role.reference': ROLE_STANDIST,
          search: value,
        })
        .list();
    }
  };

  const setExhibitorData = (modelId: id) => {
    setSelectValue(modelId);

    const selectedModel = exhibitorCollection.findBy('id', modelId) as UserModel;

    if (selectedModel) {
      setExhibitor({
        company: selectedModel.company,
        email: selectedModel.email,
        firstName: selectedModel.firstName,
        id: selectedModel.id,
        languagePreference: selectedModel.languagePreference,
        lastName: selectedModel.lastName,
        phone: selectedModel.phone,
        plainPassword: undefined,
        plainPasswordConfirmation: undefined,
        role: selectedModel.role,
      });
    }
  };

  return (
    <div className="new-modal-form">
      <Row>
        <Col span={24}>
          <div className="input-label">
            <FormattedMessageWrapper id="Rechercher un utilisateur existant :" />
          </div>
        </Col>
        <Col className="reverse-margin" lg={14} md={24} span={14}>
          <Select
            defaultActiveFirstOption={false}
            filterOption={false}
            notFoundContent={<FormattedMessageWrapper id="Aucun résultat" />}
            onChange={setExhibitorData}
            onSearch={fetchSelectData}
            placeholder="Rechercher un utilisateur"
            showArrow={false}
            showSearch
            style={{ width: '100%' }}
            value={selectValue}
          >
            {exhibitorCollection.map(user => (
              <Option key={user.id} value={user.id}>
                {user.get('company').toUpperCase()} - {user.get('firstName')}{' '}
                {user.get('lastName')} ({user.get('email')})
              </Option>
            ))}
          </Select>
        </Col>
        <Col className="reverse-margin" lg={4} md={24} span={4}>
          <div style={{ lineHeight: 2, textAlign: 'center' }}>
            <FormattedMessageWrapper id="ou" />
          </div>
        </Col>
        <Col className="reverse-margin" lg={6} md={24} span={6} style={{ textAlign: 'center' }}>
          <Button
            className="btn-clear-data"
            htmlType="button"
            key="clear"
            onClick={clearExhibitorData}
          >
            <FormattedMessageWrapper id="Nouvel utilisateur" />
          </Button>
        </Col>
      </Row>

      <Row>
        <fieldset style={{ padding: '16px 24px', width: '100%' }}>
          <legend>
            {exhibitor.id ? (
              <div>
                  <span style={{ color: '#e74c3c' }}>
                    <FormattedMessageWrapper
                      id="Modification coordonnées"
                      values={{
                        firstName: exhibitor.firstName,
                        lastName: exhibitor.lastName,
                      }}
                    />{' '}
                  </span>
              </div>
            ) : (
              <span style={{ color: '#27ae60' }}>
                  <FormattedMessageWrapper id="Création d'un nouvel utilisateur" />
                </span>
            )}
          </legend>

          <div style={{ display: 'none' }}>
            <Form.Item
              name="id"
            >
              <Input />
            </Form.Item>
            <Form.Item
              name="role"
            >
              <Input />
            </Form.Item>
          </div>

          <Row>
            <Col lg={12} md={24}>
              <Form.Item
                label={<FormattedMessageWrapper id="Société" />}
                labelCol={{ span: 24 }}
                name="company"
                rules={[
                  {
                    message: <FormattedMessageWrapper id="Information manquante" />,
                    required: true,
                  },
                ]}
              >
                <Input placeholder="Société" />
              </Form.Item>
            </Col>
          </Row>

          <Row>
            <Col lg={12} md={24}>
              <Form.Item
                label={<FormattedMessageWrapper id="Nom" />}
                labelCol={{ span: 24 }}
                name="lastName"
                rules={[
                  {
                    message: <FormattedMessageWrapper id="Information manquante" />,
                    required: true,
                  },
                ]}
              >
                <Input placeholder="Nom" />
              </Form.Item>
            </Col>
            <Col lg={12} md={24}>
              <Form.Item
                label={<FormattedMessageWrapper id="Prénom" />}
                labelCol={{ span: 24 }}
                name="firstName"
                rules={[
                  {
                    message: <FormattedMessageWrapper id="Information manquante" />,
                    required: true,
                  },
                ]}
              >
                <Input placeholder="Prénom" />
              </Form.Item>
            </Col>
          </Row>
          <Row>
            <Col lg={12} md={24}>
              <Form.Item
                label={<FormattedMessageWrapper id="Email" />}
                labelCol={{ span: 24 }}
                name="email"
                rules={[
                  {
                    message: <FormattedMessageWrapper id="Information manquante" />,
                    required: true,
                  },
                ]}
              >
                <Input placeholder="Email" />
              </Form.Item>
            </Col>
            <Col lg={12} md={24}>
              <Form.Item
                label={<FormattedMessageWrapper id="Téléphone" />}
                labelCol={{ span: 24 }}
                name="phone"
              >
                <Input placeholder="Téléphone" />
              </Form.Item>
            </Col>
          </Row>

          <Row>
            <Col lg={12} md={24}>
              <Form.Item
                label={<FormattedMessageWrapper id="Préférence linguistique" />}
                labelCol={{ span: 24 }}
                name="languagePreference"
              >
                <Select placeholder="Préférence linguistique" style={{ width: '100%' }}>
                  <Option value={null}>
                    <FormattedMessageWrapper id="Aucune" />
                  </Option>
                  <Option value={FR.value}>{FR.label}</Option>
                  <Option value={EN.value}>{EN.label}</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <div style={{ display: exhibitor.id ? 'block' : 'none' }}>
            <Row>
              <Col lg={12} md={24}>
                <Form.Item
                  label={<FormattedMessageWrapper id="Mot de passe" />}
                  labelCol={{ span: 24 }}
                  name="plainPassword"
                >
                  <Input.Password
                    // password="true"
                    placeholder="Mot de passe"
                  />
                </Form.Item>
              </Col>
              <Col lg={12} md={24}>
                <Form.Item
                  label={<FormattedMessageWrapper id="Confirmation du mot de passe" />}
                  labelCol={{ span: 24 }}
                  name="plainPasswordConfirmation"
                  rules={[
                    form => ({
                      message: (
                        <FormattedMessageWrapper id="Confirmation mot de passe incorrecte !" />
                      ),
                      validator: (_, value) => {
                        if (value && value !== form?.getFieldValue('plainPassword')) {
                          return Promise.reject();
                        }

                        return Promise.resolve();
                      },
                    }),
                  ]}
                >
                  <Input.Password
                    placeholder="Confirmation du mot de passe"
                  />
                </Form.Item>
              </Col>
            </Row>
          </div>

          {!exhibitor.id && (
            <Row>
              <Col span={24} style={{ textAlign: 'center' }}>
                <i>
                  <FormattedMessageWrapper
                    id="Un mot de passe aléatoire sera automatiquement généré pour cet utilisateur." />
                </i>
              </Col>
            </Row>
          )}
        </fieldset>
      </Row>
    </div>
  );
});

export default StepExhibitor;
